import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/apiHelper';
import { showError, showSuccess, showServerError } from '../utils/toastHelper';


function UserDashboard() {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [editMode, setEditMode] = useState({
    profile: false,
    password: false,
    address: false
  });
  const [saveStatus, setSaveStatus] = useState({
    profile: null,
    address: null,
    password: null
  });
  const [passwordError, setPasswordError] = useState(null);
  const [profileData, setProfileData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.contactInfo?.phone || ''
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [addressData, setAddressData] = useState({
    city: user?.contactInfo?.address?.city || '',
    district: user?.contactInfo?.address?.district || '',
    openAddress: user?.contactInfo?.address?.openAddress || '',
    zipCode: user?.contactInfo?.address?.zipCode || ''
  });

  const hasMinLength = passwordData.newPassword.length >= 8;
  const hasUpper = /[A-Z]/.test(passwordData.newPassword);
  const hasLower = /[a-z]/.test(passwordData.newPassword);
  const hasNumber = /\d/.test(passwordData.newPassword);
  const isPasswordValid = passwordData.newPassword.length === 0 || (hasMinLength && hasUpper && hasLower && hasNumber);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/auth/profile');
      if (response.success) {
        // User state'ini güncelle
        setUser(response.user);

        // Form verilerini güncelle
        setProfileData({
          fullName: response.user.fullName,
          email: response.user.email,
          phone: response.user.contactInfo?.phone || ''
        });
        setAddressData({
          city: response.user.contactInfo?.address?.city || '',
          district: response.user.contactInfo?.address?.district || '',
          openAddress: response.user.contactInfo?.address?.openAddress || '',
          zipCode: response.user.contactInfo?.address?.zipCode || ''
        });
      }
    } catch (error) {
      // 401 hatası için ek toast gösterme (zaten showSessionTimeout çağrıldı)
      if (error.isAuthError) {
        return;
      }
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Kullanıcı bilgileri yüklenirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleEditMode = async (section) => {
    setEditMode(prev => ({
      ...prev,
      [section]: !prev[section]
    }));

    // Reset form data when canceling edit
    if (editMode[section]) {
      // İptal edildiğinde verileri yeniden yükle
      await loadUserData();

      // Şifre verilerini sıfırla (diğerleri loadUserData'da güncellendi)
      if (section === 'password') {
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // İlk harfi büyüt ve trim yap
  const formatCityDistrict = (value) => {
    if (!value) return '';
    return value.trim().split(' ').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // İl ve ilçe için blur'da formatlama yap
  const handleAddressBlur = (e) => {
    const { name, value } = e.target;
    if (name === 'city' || name === 'district') {
      const formatted = formatCityDistrict(value);
      setAddressData(prev => ({
        ...prev,
        [name]: formatted
      }));
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Sadece değişen alanları tespit et
      const changes = {};
      const originalData = {
        fullName: user?.fullName || '',
        contactInfo: {
          phone: user?.contactInfo?.phone || ''
        }
      };

      // fullName değişti mi?
      if (profileData.fullName !== originalData.fullName) {
        changes.fullName = profileData.fullName;
      }

      // phone değişti mi?
      if (profileData.phone !== originalData.contactInfo.phone) {
        changes.contactInfo = {
          ...user.contactInfo,
          phone: profileData.phone
        };
      }

      // Değişiklik yoksa işlemi sonlandır
      if (Object.keys(changes).length === 0) {
        setEditMode(prev => ({
          ...prev,
          profile: false
        }));
        return;
      }

      const response = await apiRequest('/auth/profile', {
        method: 'PUT',
        data: changes
      });

      if (response.success) {
        // Profil bilgilerini yeniden yükle
        await loadUserData();

        setSaveStatus(prev => ({ ...prev, profile: 'success' }));
        setTimeout(() => {
          setSaveStatus(prev => ({ ...prev, profile: null }));
          setEditMode(prev => ({ ...prev, profile: false }));
        }, 2000);
      }
    } catch (error) {
      // 401 hatası için ek toast gösterme (zaten showSessionTimeout çağrıldı)
      if (error.isAuthError) {
        return;
      }
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Profil bilgileriniz güncellenirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // İl ve ilçe için formatlama yap
      const formattedData = {
        ...addressData,
        city: formatCityDistrict(addressData.city),
        district: formatCityDistrict(addressData.district)
      };

      // Formatlanmış veriyi state'e kaydet
      setAddressData(formattedData);

      // Sadece adres değişti mi kontrol et
      const originalAddress = {
        city: user?.contactInfo?.address?.city || '',
        district: user?.contactInfo?.address?.district || '',
        openAddress: user?.contactInfo?.address?.openAddress || '',
        zipCode: user?.contactInfo?.address?.zipCode || ''
      };

      const hasChanges =
        formattedData.city !== originalAddress.city ||
        formattedData.district !== originalAddress.district ||
        formattedData.openAddress !== originalAddress.openAddress ||
        formattedData.zipCode !== originalAddress.zipCode;

      if (!hasChanges) {
        setEditMode(prev => ({
          ...prev,
          address: false
        }));
        return;
      }

      // OpenAPI'ye göre address nested object olarak gönderilmeli
      const response = await apiRequest('/auth/profile', {
        method: 'PUT',
        data: {
          contactInfo: {
            ...user.contactInfo,
            address: {
              city: formattedData.city,
              district: formattedData.district,
              openAddress: formattedData.openAddress,
              country: user?.contactInfo?.address?.country || 'Türkiye',
              zipCode: formattedData.zipCode || undefined
            }
          }
        }
      });

      if (response.success) {
        // Profil bilgilerini yeniden yükle
        await loadUserData();

        setSaveStatus(prev => ({ ...prev, address: 'success' }));
        setTimeout(() => {
          setSaveStatus(prev => ({ ...prev, address: null }));
          setEditMode(prev => ({ ...prev, address: false }));
        }, 2000);
      }
    } catch (error) {
      // 401 hatası için ek toast gösterme (zaten showSessionTimeout çağrıldı)
      if (error.isAuthError) {
        return;
      }
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Adres güncellenirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError(null);

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Yeni şifreler eşleşmiyor');
      return;
    }

    if (!hasMinLength || !hasUpper || !hasLower || !hasNumber) {
      setPasswordError('Lütfen tüm şifre kurallarını karşıladığınızdan emin olun.');
      return;
    }

    try {
      setLoading(true);
      const response = await apiRequest('/auth/change-password', {
        method: 'POST',
        data: {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        }
      });

      if (response.success) {
        setSaveStatus(prev => ({ ...prev, password: 'success' }));
        setTimeout(() => {
          setSaveStatus(prev => ({ ...prev, password: null }));
          setPasswordData({
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          toggleEditMode('password');
        }, 2000);
      }
    } catch (error) {
      // 401 hatası için ek toast gösterme (zaten showSessionTimeout çağrıldı)
      if (error.isAuthError) {
        return;
      }
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Şifre değiştirme işlemi başarısız oldu');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-lg-4">
          <div className="card shadow-sm mb-4">
            <div className="card-body text-center">
              <div className="mb-3">
                <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '100px', height: '100px' }}>
                  <i className="fas fa-user fa-2x text-muted"></i>
                </div>
              </div>
              <h5 className="card-title mb-0">{user?.fullName}</h5>
              <p className="text-muted">{user?.email}</p>
              {user?.role === 'CUSTOMER' && (
                <span className="badge bg-success">Müşteri</span>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          {/* Profil Bilgileri */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="card-title mb-0">Profil Bilgileri</h5>
                <button
                  className="btn btn-sm btn-outline-olive"
                  onClick={() => toggleEditMode('profile')}
                >
                  <i className={`fas fa-${editMode.profile ? 'times' : 'edit'} me-2`}></i>
                  {editMode.profile ? 'İptal' : 'Düzenle'}
                </button>
              </div>

              {editMode.profile ? (
                <form onSubmit={handleProfileSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Ad Soyad</label>
                      <input
                        type="text"
                        className="form-control"
                        name="fullName"
                        value={profileData.fullName}
                        onChange={handleProfileChange}
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">E-posta</label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={profileData.email}
                        disabled
                        style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                      />
                      <small className="text-muted">E-posta adresi değiştirilemez</small>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Telefon</label>
                      <input
                        type="tel"
                        className="form-control"
                        name="phone"
                        value={profileData.phone}
                        onChange={handleProfileChange}
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <button type="submit" className={`btn ${saveStatus.profile === 'success' ? 'btn-success' : 'btn-orange'}`} disabled={loading || saveStatus.profile === 'success'}>
                      {saveStatus.profile === 'success' ? <><i className="fas fa-check me-2"></i>Güncellendi</> : loading ? 'Güncelleniyor...' : 'Kaydet'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label text-muted small">Ad Soyad</label>
                      <p className="mb-0">{profileData.fullName}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label text-muted small">E-posta</label>
                      <p className="mb-0">{profileData.email}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label text-muted small">Telefon</label>
                      <p className="mb-0">{profileData.phone || '-'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Adres Bilgileri */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="card-title mb-0">Adres Bilgileri</h5>
                <button
                  className="btn btn-sm btn-outline-olive"
                  onClick={() => toggleEditMode('address')}
                >
                  <i className={`fas fa-${editMode.address ? 'times' : 'edit'} me-2`}></i>
                  {editMode.address ? 'İptal' : 'Düzenle'}
                </button>
              </div>

              {editMode.address ? (
                <form onSubmit={handleAddressSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">İl <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        name="city"
                        value={addressData.city}
                        onChange={handleAddressChange}
                        onBlur={handleAddressBlur}
                        placeholder="İl"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">İlçe <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        name="district"
                        value={addressData.district}
                        onChange={handleAddressChange}
                        onBlur={handleAddressBlur}
                        placeholder="İlçe"
                        required
                      />
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Mahalle/Sokak/Kapı No <span className="text-danger">*</span></label>
                      <input
                        type="text"
                        className="form-control"
                        name="openAddress"
                        value={addressData.openAddress}
                        onChange={handleAddressChange}
                        placeholder="Mahalle, Sokak, Bina No, Daire No"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Posta Kodu</label>
                      <input
                        type="text"
                        className="form-control"
                        name="zipCode"
                        value={addressData.zipCode}
                        onChange={handleAddressChange}
                        placeholder="Posta Kodu (Opsiyonel)"
                        maxLength="5"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <button type="submit" className={`btn ${saveStatus.address === 'success' ? 'btn-success' : 'btn-orange'}`} disabled={loading || saveStatus.address === 'success'}>
                      {saveStatus.address === 'success' ? <><i className="fas fa-check me-2"></i>Güncellendi</> : loading ? 'Güncelleniyor...' : 'Kaydet'}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label text-muted small">İl</label>
                      <p className="mb-0">{addressData.city || '-'}</p>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label text-muted small">İlçe</label>
                      <p className="mb-0">{addressData.district || '-'}</p>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label text-muted small">Mahalle/Sokak/Kapı No</label>
                      <p className="mb-0">{addressData.openAddress || '-'}</p>
                    </div>
                  </div>
                  {addressData.zipCode && (
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label text-muted small">Posta Kodu</label>
                        <p className="mb-0">{addressData.zipCode}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Şifre Değiştirme */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="card-title mb-0">Şifre Değiştir</h5>
                <button
                  className="btn btn-sm btn-outline-olive"
                  onClick={() => toggleEditMode('password')}
                >
                  <i className={`fas fa-${editMode.password ? 'times' : 'key'} me-2`}></i>
                  {editMode.password ? 'İptal' : 'Şifre Değiştir'}
                </button>
              </div>

              {editMode.password ? (
                <form onSubmit={handlePasswordSubmit}>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Mevcut Şifre</label>
                      <div className="input-group">
                        <input
                          type={showCurrentPassword ? "text" : "password"}
                          className="form-control"
                          name="currentPassword"
                          value={passwordData.currentPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                        <button
                          type="button"
                          className="input-group-text bg-white"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        >
                          <i className={`fas fa-${showCurrentPassword ? 'eye-slash' : 'eye'} text-muted`}></i>
                        </button>
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Yeni Şifre</label>
                      <div className="input-group mb-2">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          className="form-control"
                          name="newPassword"
                          value={passwordData.newPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                        <button
                          type="button"
                          className="input-group-text bg-white"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                          <i className={`fas fa-${showNewPassword ? 'eye-slash' : 'eye'} text-muted`}></i>
                        </button>
                      </div>
                      
                      {passwordData.newPassword.length > 0 && (
                        <div className="mb-3 p-3 bg-light rounded" style={{ fontSize: '0.85rem' }}>
                          <div className={`d-flex align-items-center ${hasMinLength ? 'text-success' : 'text-muted'} mb-1`}>
                            <i className={`fas ${hasMinLength ? 'fa-check-circle' : 'fa-circle'} me-2`} style={{ fontSize: '0.7rem' }} /> En az 8 karakter
                          </div>
                          <div className={`d-flex align-items-center ${hasUpper ? 'text-success' : 'text-muted'} mb-1`}>
                            <i className={`fas ${hasUpper ? 'fa-check-circle' : 'fa-circle'} me-2`} style={{ fontSize: '0.7rem' }} /> En az 1 büyük harf
                          </div>
                          <div className={`d-flex align-items-center ${hasLower ? 'text-success' : 'text-muted'} mb-1`}>
                            <i className={`fas ${hasLower ? 'fa-check-circle' : 'fa-circle'} me-2`} style={{ fontSize: '0.7rem' }} /> En az 1 küçük harf
                          </div>
                          <div className={`d-flex align-items-center ${hasNumber ? 'text-success' : 'text-muted'}`}>
                            <i className={`fas ${hasNumber ? 'fa-check-circle' : 'fa-circle'} me-2`} style={{ fontSize: '0.7rem' }} /> En az 1 rakam
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="col-12">
                      <label className="form-label">Yeni Şifre Tekrar</label>
                      <div className="input-group">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className="form-control"
                          name="confirmPassword"
                          value={passwordData.confirmPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                        <button
                          type="button"
                          className="input-group-text bg-white"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          <i className={`fas fa-${showConfirmPassword ? 'eye-slash' : 'eye'} text-muted`}></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    {passwordError && (
                      <div className="alert alert-danger d-flex align-items-center mb-3 p-2" role="alert" style={{ fontSize: '0.85rem' }}>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        <div>{passwordError}</div>
                      </div>
                    )}
                    <button type="submit" className={`btn ${saveStatus.password === 'success' ? 'btn-success' : 'btn-orange'}`} disabled={loading || saveStatus.password === 'success'}>
                      {saveStatus.password === 'success' ? <><i className="fas fa-check me-2"></i>Güncellendi</> : loading ? 'Güncelleniyor...' : 'Şifreyi Değiştir'}
                    </button>
                  </div>
                </form>
              ) : (
                <p className="text-muted mb-0">
                  Şifrenizi değiştirmek için "Şifre Değiştir" butonuna tıklayın.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .btn-outline-olive {
            color: var(--kito-olive);
            border-color: var(--kito-olive);
          }
          
          .btn-outline-olive:hover {
            color: white;
            background-color: var(--kito-olive);
          }
          
          .form-label.text-muted.small {
            margin-bottom: 0.25rem;
          }
        `}
      </style>
    </div>
  );
}

export default UserDashboard; 